import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Search, ChevronDown, ChevronUp, Info, ExternalLink, AlertCircle, HelpCircle, X } from 'lucide-react';
import categoriesJson from '../data/sensitive-data-categories.json';
import stateMetadataJson from '../data/state-metadata.json';
import statesJson from '../data/states.json';

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
  }
}

const trackEvent = (eventName: string, params?: Record<string, unknown>) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, params);
  }
};

// Define the type for sensitive data category
interface SensitiveDataCategory {
  states: string[];
  description: string;
  note?: string;
}

// State metadata interface
interface StateMetadata {
  effectiveDate: string;
  lawName: string;
  status: 'active' | 'upcoming';
  specialRestrictions?: string[];
  childrenAgeDefinition?: string;
  requiresDPA?: boolean;
  prohibitsSensitiveDataSale?: boolean;
  strictnessLevel?: 'standard' | 'strict' | 'very-strict';
  requiresUOO?: boolean;
  applicabilityThreshold: string;
  enforcementAgency: string;
  maxPenalty: string;
  curePeriod: string;
  privateRightOfAction?: boolean;
  lastVerified: string;
}

// Data lives in src/data/*.json so the build-time page generator (scripts/prerender.mjs), llms.txt and the
// dataset downloads read the same source as the app. Edit the JSON, not this file, to update the data.
const sensitiveDataCategories = categoriesJson as unknown as Record<string, SensitiveDataCategory>;
const stateMetadata = stateMetadataJson as unknown as Record<string, StateMetadata>;
const allStates: string[] = statesJson.map((s) => s.code);
const stateSlugToCode: Record<string, string> = Object.fromEntries(statesJson.map((s) => [s.slug, s.code]));
const categorySlug = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
// Entity pages are prerendered at /state/<slug>/ and /category/<slug>/; the app opens the matching view there.
const entityFromPath = () => {
  const m = window.location.pathname.match(/^\/(state|category)\/([a-z0-9-]+)\/?$/);
  if (!m) return null;
  if (m[1] === 'state') return { state: stateSlugToCode[m[2]] || '' };
  const cat = Object.keys(categoriesJson).find((c) => categorySlug(c) === m[2]);
  return { category: cat || '' };
};
const initialEntity = typeof window !== 'undefined' ? entityFromPath() : null;

const SensitiveDataComplianceTool = () => {
  const [searchTerm, setSearchTerm] = useState<string>(() =>
    new URLSearchParams(window.location.search).get('search') || (initialEntity && 'category' in initialEntity ? initialEntity.category : '') || ''
  );
  const [selectedState, setSelectedState] = useState<string>(() =>
    new URLSearchParams(window.location.search).get('state') || (initialEntity && 'state' in initialEntity ? initialEntity.state : '') || ''
  );
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [viewMode, setViewMode] = useState(() =>
    new URLSearchParams(window.location.search).get('view') || 'category'
  );
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!localStorage.getItem('pii_welcome_v3')) {
      setShowWelcome(true);
    }
  }, []);

  const dismissWelcome = (openHowItWorks = false) => {
    localStorage.setItem('pii_welcome_v3', '1');
    setShowWelcome(false);
    if (openHowItWorks) setShowHowItWorks(true);
  };

  // Dynamic SEO updates
  useEffect(() => {
    let title = 'PII Compliance Navigator | U.S. State Privacy Law Sensitive Data Categories';
    let description = 'Comprehensive tool to explore which U.S. states classify different types of data as sensitive under privacy laws. Navigate compliance requirements across 23 enacted state laws (19 active, 4 taking effect 2027-2028) including CCPA, CDPA, CTDPA, MODPA and more. Updated Q3 2026.';

    if (selectedState) {
      title = `${selectedState} Privacy Law Sensitive Data Categories | PII Compliance Navigator`;
      description = `Explore sensitive data categories under ${selectedState} privacy laws. Find out which types of personal information are classified as sensitive in ${selectedState}.`;
    } else if (searchTerm) {
      title = `Search: "${searchTerm}" | PII Compliance Navigator`;
      description = `Search results for "${searchTerm}" in U.S. state privacy law sensitive data categories. Find compliance requirements across multiple states.`;
    } else if (viewMode === 'state') {
      title = 'State View | PII Compliance Navigator - Privacy Laws by State';
      description = 'Browse sensitive data categories organized by U.S. state. Compare privacy law requirements across AL, CA, CO, CT, DE, IN, IA, KY, LA, MD, MN, MT, NE, NH, NJ, OK, OR, RI, TN, TX, UT, VA, VT.';
    }

    document.title = title;
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', description);
    }
  }, [selectedState, searchTerm, viewMode]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (searchTerm) params.set('search', searchTerm);
    if (selectedState) params.set('state', selectedState);
    if (viewMode !== 'category') params.set('view', viewMode);
    const qs = params.toString();
    // On a prerendered entity page keep its clean URL while the selection still matches it.
    if (initialEntity) {
      const stillOnEntity = ('state' in initialEntity ? selectedState === initialEntity.state && !searchTerm : searchTerm === initialEntity.category && !selectedState) && viewMode === 'category';
      if (stillOnEntity) return;
      window.history.replaceState(null, '', qs ? `/?${qs}` : '/');
      return;
    }
    window.history.replaceState(null, '', qs ? `?${qs}` : window.location.pathname);
  }, [searchTerm, selectedState, viewMode]);

  // Filter categories based on search
  const filteredCategories = useMemo(() => {
    return Object.entries(sensitiveDataCategories).filter(([category, data]) => {
      const matchesSearch = category.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          data.description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesState = !selectedState || data.states.includes(selectedState);
      return matchesSearch && matchesState;
    });
  }, [searchTerm, selectedState]);

  // Get categories for a specific state
  const getCategoriesForState = (state: string): string[] => {
    return Object.entries(sensitiveDataCategories)
      .filter(([_, data]) => data.states.includes(state))
      .map(([category, _]) => category);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      if (value.length >= 3) {
        trackEvent('search', { search_term: value, content_type: 'pii_category' });
      }
    }, 800);
  };

  const handleStateChange = (state: string) => {
    setSelectedState(state);
    if (state) {
      trackEvent('filter_state', { state_code: state, law_name: stateMetadata[state]?.lawName });
    }
  };

  const handleViewChange = (mode: string) => {
    setViewMode(mode);
    trackEvent('view_change', { view_mode: mode });
  };

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    const isExpanding = !newExpanded.has(category);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
    if (isExpanding) {
      trackEvent('category_expand', { category_name: category });
    }
  };

  const expandAll = () => setExpandedCategories(new Set(filteredCategories.map(([cat]) => cat)));
  const collapseAll = () => setExpandedCategories(new Set());

  const sponsors = [
    { name: "Global Compliance Map", url: "https://globalcompliancemap.com/" },
    { name: "Compliance Hub Wiki", url: "https://www.compliancehub.wiki/" },
    { name: "Baseline Compliance Hub", url: "https://baseline.compliancehub.wiki/" },
    { name: "Estimate Compliance Hub", url: "https://estimate.compliancehub.wiki/" }
  ];

  const footnotes = [
    "California (CCPA § 1798.121(d)): Sensitive personal information collected or processed without the purpose of inferring characteristics about a consumer must still be treated as personal information — not sensitive — for opt-out purposes. CA AB 45 (effective Jan 2026) further prohibits collection of personal data near family planning centers and bans geofencing around healthcare facilities.",
    "California thresholds and penalties are CPI-adjusted: the revenue threshold is $26,625,000 and penalties are $2,663 per violation / $7,988 per intentional or minor-related violation (effective Jan 1, 2025). CPPA regulations on automated decision-making technology, risk assessments and cybersecurity audits took effect Jan 1, 2026, and data brokers must process Delete Act (DROP) requests from Aug 1, 2026.",
    "Colorado Privacy Act Rule 6.10: Controllers must obtain affirmative opt-in consent before processing sensitive data inferences, even where the underlying data was collected lawfully. Maryland (HB 711, Jul 1, 2026) and Delaware (HB 380, Jan 1, 2027) now write sensitive inferences directly into their sensitive data definitions.",
    "California's biometric and genetic data definitions do not require that data be processed for the purpose of unique identification — broader than most other state definitions. Vermont (2028) likewise drops the unique-identification qualifier.",
    "Maryland (MODPA) applies a 'strictly necessary' standard to sensitive data processing: collection and use must be strictly necessary to provide the requested product or service. This is stricter than the 'consent' or 'opt-out' frameworks in other states. MODPA enforcement began April 1, 2026.",
    "Sensitive data sale bans: Maryland prohibits the sale of sensitive personal data outright, and as of June 30, 2026 New Jersey (A5328) prohibits any entity — regardless of size — from selling or licensing sensitive data, with no consent exception and civil penalties of $50,000 per record. Delaware will require consent plus a 'strictly necessary' showing from Jan 1, 2027.",
    "Precise geolocation: Oregon (HB 2008, Jan 1, 2026), Virginia (SB 338, Jul 1, 2026) and Connecticut (SB 4, Oct 1, 2026) ban the sale of precise geolocation data entirely. Maryland, Louisiana and Oregon define 'precise' as a 1,750-foot radius.",
    "Connecticut SB 1295 (effective Jul 1, 2026) is the most significant amendment of the year: applicability drops to 35,000 consumers (or ANY sensitive-data processing or ANY sale of personal data), sensitive data gains neural data, government ID numbers, financial credentials, disability and treatment data, privacy notices must disclose LLM-training use, and profiling impact assessments are required from Aug 1, 2026. Minors aged 13–17 may not be targeted with ads or have data sold regardless of consent.",
    "Neural data is separately protected in California (SB 1223, effective Jan 2025), Connecticut (SB 1295, Jul 2026) and Vermont (Jan 2028); Delaware adds it Jan 1, 2027. Colorado's 'biological data' category explicitly includes neural data derived from technological processing.",
    "Data Protection Assessments: New Jersey and Maryland require DPAs specifically for sensitive data processing; Louisiana (2027) and Vermont (2028) will too. Alabama (2027) is notable for requiring no DPAs at all and no duty to honor GPC signals.",
    "Children's data: Most states define a 'child' as under 13 (COPPA threshold). Connecticut extends blanket protections to minors 13–17; Colorado (Oct 2025) and Montana (Oct 2025) require consent for targeted ads, sale and profiling of under-18s; Oregon bans sale of under-16 data; New Hampshire bans sale of under-13 data from Jan 1, 2027 (HB 1460); Alabama (2027) requires consent for 13–15s.",
    "Cure periods are disappearing: mandatory cure rights have expired in Colorado, Connecticut, Montana (Oct 2025), Delaware and New Hampshire (Dec 31, 2025), Oregon (Jan 1, 2026), Minnesota (Jan 31, 2026) and New Jersey (Jul 15, 2026). Permanent cure periods remain in IN, IA, KY, NE, RI, TN, TX, UT, VA; Oklahoma (30 days) and Alabama (45 days) will be permanent; Louisiana's sunsets Jul 31, 2027 and Vermont's Jun 30, 2029.",
    "Universal Opt-Out Mechanism (UOO / Global Privacy Control): 12 of the 19 active states require businesses to honor GPC browser signals as a valid opt-out of sale/sharing — CA, CO, CT, DE, MD, MN, MT, NE, NH, NJ, OR, TX. Vermont will require it in 2028; Oklahoma, Louisiana and Alabama do not mandate it. California AB 566 (Jan 1, 2027) will require browsers themselves to offer an opt-out preference signal.",
    "Washington's My Health Data Act (effective 2024), Florida's Digital Bill of Rights ($1B+ revenue threshold) and Arkansas's Children and Teens' Online Privacy Protection Act (Jul 1, 2026, minors only) are sector-specific or threshold-limited laws not reflected in this tool's state grid — consult those statutes separately if applicable.",
    "This tool reflects enacted state comprehensive laws — 19 in effect plus Oklahoma and Louisiana (Jan 1, 2027), Alabama (May 1, 2027) and Vermont (Jan 1, 2028). Massachusetts (Senate passed Sept 2025, House passed Jun 2026, conference committee ongoing), Pennsylvania (HB 78 in Senate) and Michigan (SB 359) have active bills — monitor for enactment."
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">

      {/* Welcome Modal — first visit only */}
      {showWelcome && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => dismissWelcome()}>
          <div className="bg-slate-100 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-7 rounded-t-2xl">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-blue-200 text-sm font-medium uppercase tracking-wide mb-1">Welcome to</p>
                  <h2 className="text-2xl font-bold mb-1">PII Compliance Navigator</h2>
                  <p className="text-blue-100 text-sm">Your free reference for U.S. state sensitive data law</p>
                </div>
                <button onClick={() => dismissWelcome()} className="text-white hover:bg-white/20 rounded-full p-2 transition-colors flex-shrink-0">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-5">
              {/* What it does */}
              <div>
                <h3 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                  <Info className="w-4 h-4 text-blue-600" />
                  What This Tool Does
                </h3>
                <p className="text-sm text-gray-600 leading-relaxed">
                  The <strong>PII Compliance Navigator</strong> maps which types of personal data are classified as
                  <strong> "sensitive"</strong> under each U.S. state's comprehensive privacy law — so you can quickly answer
                  "does this state require special handling for this data type?" without reading 23 different statutes.
                  It covers <strong>36 sensitive data categories</strong> across all 19 active state laws plus the four
                  enacted in 2026 (Oklahoma, Alabama, Louisiana, Vermont) that take effect 2027–2028.
                </p>
              </div>

              {/* Who it's for */}
              <div className="grid grid-cols-2 gap-2">
                {[
                  { role: "Privacy Attorneys", detail: "Fast statutory cross-reference" },
                  { role: "Compliance Officers", detail: "State-by-state gap analysis" },
                  { role: "DPOs & Security Teams", detail: "Data classification decisions" },
                  { role: "Product & Engineering", detail: "Know what triggers consent" },
                ].map(({ role, detail }) => (
                  <div key={role} className="bg-slate-200 rounded-lg p-3 border border-slate-300">
                    <p className="text-xs font-semibold text-gray-800">{role}</p>
                    <p className="text-xs text-gray-500">{detail}</p>
                  </div>
                ))}
              </div>

              {/* 2026 updates */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <h3 className="font-bold text-emerald-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  Q3 2026 Update — What's New (September 2026)
                </h3>
                <ul className="space-y-1.5 text-sm text-emerald-800">
                  <li className="flex items-start gap-2"><span className="mt-0.5 text-emerald-600">✓</span><span><strong>Four new states enacted in 2026:</strong> Oklahoma &amp; Louisiana (Jan 1, 2027), Alabama (May 1, 2027), Vermont (Jan 1, 2028) — now tracked as "Upcoming" with full sensitive-data mapping</span></li>
                  <li className="flex items-start gap-2"><span className="mt-0.5 text-emerald-600">✓</span><span><strong>Connecticut SB 1295</strong> (Jul 1, 2026): threshold cut to 35K, any sensitive-data processing triggers the law, neural data &amp; government IDs added, LLM-training disclosure required</span></li>
                  <li className="flex items-start gap-2"><span className="mt-0.5 text-emerald-600">✓</span><span><strong>New Jersey</strong> now bans the sale of sensitive data for everyone (A5328, Jun 30, 2026, $50K/record) — joins Maryland as "Very Strict"</span></li>
                  <li className="flex items-start gap-2"><span className="mt-0.5 text-emerald-600">✓</span><span><strong>Geolocation sale bans</strong> in Oregon (Jan 2026), Virginia (Jul 2026) and Connecticut (Oct 1, 2026); Maryland adds inferred sensitive data (HB 711)</span></li>
                  <li className="flex items-start gap-2"><span className="mt-0.5 text-emerald-600">✓</span><span><strong>Cure periods gone</strong> in DE, NH (Dec 2025), MN (Jan 2026), MT (Oct 2025) and NJ (Jul 2026) — regulators can now act on day one</span></li>
                  <li className="flex items-start gap-2"><span className="mt-0.5 text-emerald-600">✓</span><span><strong>California:</strong> DROP data-broker deletion live Aug 1, 2026; CPI-adjusted thresholds ($26.625M) and penalties ($2,663 / $7,988); Delaware HB 380 signed Sept 2, 2026</span></li>
                  <li className="flex items-start gap-2"><span className="mt-0.5 text-emerald-600">✓</span><span>New categories: <strong>government ID numbers</strong> and <strong>sensitive inferences</strong>; watch list updated for Massachusetts conference committee, Michigan &amp; Pennsylvania</span></li>
                </ul>
              </div>

              {/* CTA row */}
              <div className="flex flex-col sm:flex-row gap-3 pt-1">
                <button
                  onClick={() => dismissWelcome(true)}
                  className="flex-1 px-4 py-3 bg-white border-2 border-blue-600 text-blue-600 rounded-lg font-semibold text-sm hover:bg-blue-50 transition-colors flex items-center justify-center gap-2"
                >
                  <HelpCircle className="w-4 h-4" />
                  Show Me How It Works
                </button>
                <button
                  onClick={() => dismissWelcome()}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold text-sm hover:from-blue-700 hover:to-purple-700 transition-colors"
                >
                  Start Exploring →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* How It Works Modal */}
      {showHowItWorks && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowHowItWorks(false)}>
          <div className="bg-slate-100 rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-lg">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-2xl font-bold mb-2">How It Works</h2>
                  <p className="text-blue-100">Your guide to navigating U.S. state privacy compliance</p>
                </div>
                <button
                  onClick={() => setShowHowItWorks(false)}
                  className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* What This Tool Does */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <Info className="w-5 h-5 text-blue-600" />
                  What This Tool Does
                </h3>
                <p className="text-gray-700 mb-3">
                  The PII Compliance Navigator helps you understand which types of personal information are classified as "sensitive data"
                  under various U.S. state privacy laws. With 23 states now having enacted comprehensive privacy legislation — 19 active
                  today and four more (OK, LA, AL, VT) taking effect between January 2027 and January 2028 — staying compliant can be complex.
                  This tool simplifies that complexity.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-900">
                    <strong>Coverage:</strong> 36 distinct sensitive data categories across 23 U.S. states including
                    CA (CCPA/CPRA), CO, CT, DE, IN, IA, KY, MD, MN, MT, NE, NH, NJ, OR, RI, TN, TX, UT, and VA (all active),
                    plus OK, LA, AL and VT (enacted 2026, effective 2027–2028). Content verified September 2026 (Q3 2026 update).
                  </p>
                </div>
              </div>

              {/* How to Use */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">How to Use This Navigator</h3>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Search by Category</h4>
                      <p className="text-sm text-gray-600">
                        Use the search bar to find specific data types (e.g., "biometric", "health", "geolocation").
                        Click on any category to see which states classify it as sensitive.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Filter by State</h4>
                      <p className="text-sm text-gray-600">
                        Select a specific state to see all sensitive data categories that apply under that state's law.
                        Perfect for understanding state-specific compliance requirements.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold">3</div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Switch Views</h4>
                      <p className="text-sm text-gray-600">
                        Toggle between "By Category" view (see all categories and their states) and "By State" view
                        (see detailed information about each state's law including effective dates and special requirements).
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Key Features */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Key Features</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <h4 className="font-semibold text-green-900 text-sm mb-1">Compliance Status</h4>
                    <p className="text-xs text-green-800">19 laws active, 4 upcoming (OK, LA, AL, VT) — effective dates shown per state</p>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                    <h4 className="font-semibold text-orange-900 text-sm mb-1">Strictness Levels</h4>
                    <p className="text-xs text-orange-800">Identify states with very strict, strict, or standard requirements</p>
                  </div>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <h4 className="font-semibold text-red-900 text-sm mb-1">Sale Restrictions</h4>
                    <p className="text-xs text-red-800">Know which states prohibit sale of sensitive data (e.g., Maryland)</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <h4 className="font-semibold text-blue-900 text-sm mb-1">DPA Requirements</h4>
                    <p className="text-xs text-blue-800">States requiring Data Protection Assessments for sensitive data</p>
                  </div>
                  <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
                    <h4 className="font-semibold text-violet-900 text-sm mb-1">UOO / GPC Required</h4>
                    <p className="text-xs text-violet-800">12 active states require honoring Global Privacy Control browser signals as opt-out (Vermont joins in 2028)</p>
                  </div>
                </div>
              </div>

              {/* Understanding the Icons */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Understanding the Icons & Badges</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">Active</span>
                    <span className="text-gray-600">Law is currently in effect</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs font-medium">Upcoming</span>
                    <span className="text-gray-600">Law not yet in effect</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">⏱</span>
                    <span className="text-gray-600">Upcoming law (not yet in effect)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg">🚫</span>
                    <span className="text-gray-600">State prohibits sale of sensitive data</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">Very Strict</span>
                    <span className="text-gray-600">Enhanced compliance requirements (e.g., Maryland's "strictly necessary" standard)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">Cure: 30 days</span>
                    <span className="text-gray-600">State gives businesses a cure period before enforcement action</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium">Cure: None</span>
                    <span className="text-gray-600">No cure period — enforcement can begin immediately</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">⚖️</span>
                    <span className="text-gray-600">Private right of action — consumers can sue directly (CA data breaches only)</span>
                  </div>
                </div>
              </div>

              {/* Why This Matters */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
                <h3 className="text-lg font-bold text-purple-900 mb-2">Why This Matters</h3>
                <p className="text-sm text-purple-800">
                  Misclassifying sensitive data can lead to compliance violations, fines, and loss of consumer trust.
                  Different states have different definitions—what's sensitive in Maryland might not be in Utah.
                  This tool helps you navigate those differences quickly and accurately.
                </p>
              </div>

              {/* CTA */}
              <div className="text-center pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowHowItWorks(false)}
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-colors"
                >
                  Got It! Start Exploring
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-blue-700 to-purple-700 text-white px-8 pt-8 pb-6">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="text-blue-200 text-xs font-semibold uppercase tracking-widest mb-2">Free Compliance Reference · Q3 2026 Update · September 2026</p>
              <h1 className="text-3xl font-bold mb-2 leading-tight">Sensitive Data Compliance Navigator</h1>
              <p className="text-blue-100 text-sm">Which personal data types require special handling under each U.S. state privacy law?</p>
            </div>
            <button
              onClick={() => setShowHowItWorks(true)}
              className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-sm font-medium border border-white/30"
            >
              <HelpCircle className="w-4 h-4" />
              How It Works
            </button>
          </div>
        </div>

        {/* Hero copy — below gradient, inside same card */}
        <div className="bg-slate-100 px-8 py-5 border-t border-blue-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2">
              <h2 className="text-base font-semibold text-gray-900 mb-2">What is the PII Compliance Navigator?</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                U.S. state privacy laws don't agree on what counts as "sensitive" personal information. Data that requires
                explicit <strong>opt-in consent</strong> in Maryland may only need an opt-out in California — and may not be
                classified as sensitive at all in Utah. With 19 state comprehensive privacy laws active and four more enacted for
                2027–2028, compliance teams need a fast way to map <strong>data type → state → obligation</strong>.
              </p>
              <p className="text-sm text-gray-600 leading-relaxed mt-2">
                This tool gives you that map. Search any category — biometric data, geolocation, health records, children's data,
                neural data — and instantly see which states regulate it as sensitive, under which law, and at what strictness level.
                Filter by state to build a compliance checklist in seconds.
              </p>
            </div>
            <div className="space-y-2">
              <div className="bg-slate-200 rounded-lg p-3 border border-slate-300">
                <p className="text-xs font-bold text-gray-700 mb-0.5">19 Active + 4 Upcoming State Laws</p>
                <p className="text-xs text-gray-500">OK &amp; LA (Jan 2027), AL (May 2027), VT (Jan 2028) now mapped</p>
              </div>
              <div className="bg-slate-200 rounded-lg p-3 border border-slate-300">
                <p className="text-xs font-bold text-gray-700 mb-0.5">36 Sensitive Data Categories</p>
                <p className="text-xs text-gray-500">From racial origin to neural data and sensitive inferences — every defined type</p>
              </div>
              <div className="bg-slate-200 rounded-lg p-3 border border-slate-300">
                <p className="text-xs font-bold text-gray-700 mb-0.5">Strictness &amp; Obligation Flags</p>
                <p className="text-xs text-gray-500">Sale bans, DPA requirements, consent standards per state</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-slate-100 rounded-xl shadow-sm border border-slate-300 p-5 space-y-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search categories or descriptions..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
              />
            </div>
          </div>
          
          <select
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={selectedState}
            onChange={(e) => handleStateChange(e.target.value)}
          >
            <option value="">All States</option>
            {allStates.map(state => (
              <option key={state} value={state}>{state}{stateMetadata[state]?.status === 'upcoming' ? ' (2027+)' : ''}</option>
            ))}
          </select>

          <div className="flex gap-2">
            <button
              onClick={() => handleViewChange('category')}
              className={`px-4 py-2 rounded-lg transition-colors ${viewMode === 'category' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              By Category
            </button>
            <button
              onClick={() => handleViewChange('state')}
              className={`px-4 py-2 rounded-lg transition-colors ${viewMode === 'state' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
            >
              By State
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={expandAll}
              className="px-3 py-2 rounded-lg text-sm bg-slate-200 text-gray-700 hover:bg-slate-300 transition-colors"
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="px-3 py-2 rounded-lg text-sm bg-slate-200 text-gray-700 hover:bg-slate-300 transition-colors"
            >
              Collapse All
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-4">
        {viewMode === 'category' ? (
          // Category View
          filteredCategories.length > 0 ? (
            filteredCategories.map(([category, data]) => (
              <div key={category} className="bg-slate-100 rounded-xl shadow-sm border border-slate-300 overflow-hidden">
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full px-6 py-4 text-left hover:bg-gray-50 transition-colors flex items-center justify-between"
                >
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{category}</h3>
                    <p className="text-sm text-gray-600 mt-1">{data.description}</p>
                    {data.note && (
                      <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        {data.note}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                      {data.states.length} state{data.states.length !== 1 ? 's' : ''}
                    </span>
                    {expandedCategories.has(category) ? 
                      <ChevronUp className="w-5 h-5 text-gray-400" /> : 
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    }
                  </div>
                </button>
                
                {expandedCategories.has(category) && (
                  <div className="px-6 pb-4 border-t border-gray-100">
                    <div className="mt-4">
                      <p className="text-sm font-medium text-gray-700 mb-3">States that classify this as sensitive data:</p>
                      <div className="flex flex-wrap gap-2">
                        {data.states.map(state => {
                          const metadata = stateMetadata[state];
                          const isActive = metadata.status === 'active';
                          const isVeryStrict = metadata.strictnessLevel === 'very-strict';
                          const isStrict = metadata.strictnessLevel === 'strict';
                          return (
                            <div key={state} className="relative group">
                              <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-sm font-medium ${
                                isVeryStrict ? 'bg-red-100 text-red-800 border border-red-300' :
                                isStrict ? 'bg-orange-100 text-orange-800 border border-orange-300' :
                                'bg-purple-100 text-purple-800'
                              }`}>
                                {state}
                                {!isActive && <span className="text-xs">⏱</span>}
                                {metadata.prohibitsSensitiveDataSale && <span className="text-xs">🚫</span>}
                                {metadata.requiresUOO && <span className="text-xs">📡</span>}
                              </span>
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                                <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                                  {metadata.lawName} — {metadata.effectiveDate}
                                  {!isActive && <div>⏱ Upcoming</div>}
                                  {metadata.prohibitsSensitiveDataSale && <div>🚫 No Sale of Sensitive Data</div>}
                                  {metadata.requiresUOO && <div>📡 Universal Opt-Out Required</div>}
                                  {metadata.requiresDPA && <div>📋 DPA Required</div>}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="mt-3 text-xs text-gray-500 flex flex-wrap gap-3">
                        <span>🚫 = Prohibits sale of sensitive data</span>
                        <span>📡 = Universal Opt-Out (GPC) required</span>
                        <span>📋 = DPA required</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="bg-slate-100 rounded-xl border border-slate-300 p-8 text-center text-gray-500">
              No categories found matching your search criteria.
            </div>
          )
        ) : (
          // State View
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {allStates.map(state => {
              const categories = getCategoriesForState(state);
              const metadata = stateMetadata[state];
              const strictnessColor = metadata.strictnessLevel === 'very-strict' ? 'bg-red-100 text-red-800' :
                                      metadata.strictnessLevel === 'strict' ? 'bg-orange-100 text-orange-800' :
                                      'bg-green-100 text-green-800';
              return (
                <div key={state} className="bg-slate-100 rounded-xl shadow-sm border border-slate-300 p-6 hover:shadow-md hover:border-blue-300 transition-all">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{state}</h3>
                      <p className="text-xs text-gray-500">{metadata.lawName}</p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${metadata.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                      {metadata.status === 'active' ? 'Active' : 'Upcoming'}
                    </span>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-gray-600">Effective:</span>
                      <span className="font-medium text-gray-900">{metadata.effectiveDate}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${strictnessColor}`}>
                        {metadata.strictnessLevel === 'very-strict' ? 'Very Strict' :
                         metadata.strictnessLevel === 'strict' ? 'Strict' : 'Standard'}
                      </span>
                      {metadata.prohibitsSensitiveDataSale && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                          No Sale
                        </span>
                      )}
                      {metadata.requiresDPA && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          DPA Required
                        </span>
                      )}
                      {metadata.requiresUOO && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-800">
                          UOO
                        </span>
                      )}
                    </div>

                    {metadata.specialRestrictions && metadata.specialRestrictions.length > 0 && (
                      <div className="mt-2 p-2 bg-amber-50 rounded border border-amber-200">
                        <p className="text-xs font-semibold text-amber-900 mb-1">Special Requirements:</p>
                        {metadata.specialRestrictions.map((restriction, idx) => (
                          <p key={idx} className="text-xs text-amber-800">• {restriction}</p>
                        ))}
                      </div>
                    )}

                    {/* Applicability threshold */}
                    <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-100">
                      <p className="text-xs font-semibold text-blue-900 mb-0.5">Applies to:</p>
                      <p className="text-xs text-blue-800">{metadata.applicabilityThreshold}</p>
                    </div>

                    {/* Enforcement + penalty */}
                    <div className="mt-1.5 p-2 bg-slate-50 rounded border border-slate-200">
                      <p className="text-xs text-gray-700">
                        <span className="font-semibold">Enforcement:</span> {metadata.enforcementAgency}
                      </p>
                      <p className="text-xs text-gray-700">
                        <span className="font-semibold">Max penalty:</span> {metadata.maxPenalty}
                      </p>
                    </div>

                    {/* Cure period + private right of action badges */}
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        metadata.curePeriod === 'None' || metadata.curePeriod.startsWith('None')
                          ? 'bg-red-100 text-red-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        Cure: {metadata.curePeriod}
                      </span>
                      {metadata.privateRightOfAction && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                          ⚖️ Private Right of Action
                        </span>
                      )}
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 mb-2 font-medium">{categories.length} sensitive data categories</p>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {categories.map(cat => (
                      <div key={cat} className="text-xs text-gray-700 py-1 border-b border-gray-100 last:border-0">
                        {cat}
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 mt-2 pt-2 border-t border-slate-200">
                    Last verified: {metadata.lastVerified}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Statistics */}
      <div className="bg-slate-100 border border-slate-300 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Info className="w-5 h-5 text-blue-600" />
          Quick Statistics
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-slate-200 border border-slate-300 rounded-lg p-4">
            <p className="text-2xl font-bold text-blue-600">{Object.keys(sensitiveDataCategories).length}</p>
            <p className="text-sm text-gray-600">Total Categories</p>
          </div>
          <div className="bg-slate-200 border border-slate-300 rounded-lg p-4">
            <p className="text-2xl font-bold text-purple-600">{allStates.length}</p>
            <p className="text-sm text-gray-600">States Covered (19 active + 4 upcoming)</p>
          </div>
          <div className="bg-slate-200 border border-slate-300 rounded-lg p-4">
            <p className="text-2xl font-bold text-green-600">
              {Object.values(stateMetadata).filter(m => m.status === 'active').length}
            </p>
            <p className="text-sm text-gray-600">Laws Active Now</p>
          </div>
          <div className="bg-slate-200 border border-slate-300 rounded-lg p-4">
            <p className="text-2xl font-bold text-violet-600">
              {Object.values(stateMetadata).filter(m => m.requiresUOO).length}
            </p>
            <p className="text-sm text-gray-600">Require UOO (GPC)</p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-slate-200 border border-slate-300 rounded-lg p-4 border-l-4 border-l-red-500">
            <p className="text-2xl font-bold text-red-600">
              {Object.values(stateMetadata).filter(m => m.strictnessLevel === 'very-strict').length}
            </p>
            <p className="text-sm text-gray-600">Very Strict States</p>
            <p className="text-xs text-gray-500 mt-1">
              {Object.entries(stateMetadata).filter(([_, m]) => m.strictnessLevel === 'very-strict').map(([state]) => state).join(', ')}
            </p>
          </div>
          <div className="bg-slate-200 border border-slate-300 rounded-lg p-4 border-l-4 border-l-blue-500">
            <p className="text-2xl font-bold text-blue-600">
              {Object.values(stateMetadata).filter(m => m.requiresDPA).length}
            </p>
            <p className="text-sm text-gray-600">Require DPA</p>
            <p className="text-xs text-gray-500 mt-1">
              {Object.entries(stateMetadata).filter(([_, m]) => m.requiresDPA).map(([state]) => state).join(', ')}
            </p>
          </div>
          <div className="bg-slate-200 border border-slate-300 rounded-lg p-4 border-l-4 border-l-purple-500">
            <p className="text-2xl font-bold text-purple-600">
              {Object.values(stateMetadata).filter(m => m.prohibitsSensitiveDataSale).length}
            </p>
            <p className="text-sm text-gray-600">Ban Data Sales</p>
            <p className="text-xs text-gray-500 mt-1">
              {Object.entries(stateMetadata).filter(([_, m]) => m.prohibitsSensitiveDataSale).map(([state]) => state).join(', ')}
            </p>
          </div>
          <div className="bg-slate-200 border border-slate-300 rounded-lg p-4 border-l-4 border-l-violet-500">
            <p className="text-2xl font-bold text-violet-600">
              {Object.values(stateMetadata).filter(m => m.requiresUOO).length}
            </p>
            <p className="text-sm text-gray-600">Require UOO / GPC</p>
            <p className="text-xs text-gray-500 mt-1">
              {Object.entries(stateMetadata).filter(([_, m]) => m.requiresUOO).map(([state]) => state).join(', ')}
            </p>
          </div>
        </div>
      </div>

      {/* Important Notes */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-amber-900 mb-3 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          Important Legal Notes
        </h3>
        <ul className="space-y-2 text-sm text-amber-800">
          {footnotes.map((note, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <span className="text-amber-600 mt-0.5">•</span>
              <span>{note}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Related Tools */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Related Privacy Tools:</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <a
            href="https://biometric.myprivacy.blog/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors rounded-lg p-3 text-sm"
          >
            <span className="truncate">Biometric Privacy Tool</span>
            <ExternalLink className="w-4 h-4 flex-shrink-0" />
          </a>
          <a
            href="https://notification.breached.company/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors rounded-lg p-3 text-sm"
          >
            <span className="truncate">Breach Notification Tool</span>
            <ExternalLink className="w-4 h-4 flex-shrink-0" />
          </a>
          <a
            href="https://privacyrights.compliancehub.wiki/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors rounded-lg p-3 text-sm"
          >
            <span className="truncate">Privacy Rights Hub</span>
            <ExternalLink className="w-4 h-4 flex-shrink-0" />
          </a>
          <a
            href="https://childrenprivacylaws.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors rounded-lg p-3 text-sm"
          >
            <span className="truncate">Children's Privacy Laws</span>
            <ExternalLink className="w-4 h-4 flex-shrink-0" />
          </a>
        </div>
      </div>

      {/* Sponsors */}
      <div className="bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Sponsored by:</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {sponsors.map((sponsor, idx) => (
            <a
              key={idx}
              href={sponsor.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-white/10 hover:bg-white/20 transition-colors rounded-lg p-3 text-sm"
            >
              <span className="truncate">{sponsor.name}</span>
              <ExternalLink className="w-4 h-4 flex-shrink-0" />
            </a>
          ))}
        </div>
      </div>

      {/* 2026 Timeline & Upcoming Deadlines */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          2026 Privacy Law Timeline &amp; Upcoming Deadlines
        </h3>
        <p className="text-xs text-green-100 mb-4">19 comprehensive state privacy laws are in effect; four more were enacted in 2026. Verified September 2026 (Q3 2026 update).</p>

        <p className="text-xs font-semibold text-green-100 uppercase tracking-wide mb-2">Took Effect in 2026</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4">
          <div className="bg-white/10 rounded-lg p-3">
            <p className="font-bold text-sm">Indiana · Kentucky · Rhode Island ✓</p>
            <p className="text-xs text-green-100">New comprehensive laws</p>
            <p className="text-xs">January 1, 2026</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <p className="font-bold text-sm">Oregon HB 2008 ✓</p>
            <p className="text-xs text-green-100">Sale of geolocation &amp; under-16 data banned</p>
            <p className="text-xs">January 1, 2026</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <p className="font-bold text-sm">Maryland enforcement ✓</p>
            <p className="text-xs text-green-100">MODPA enforceable · ⚠️ Very Strict</p>
            <p className="text-xs">April 1, 2026</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <p className="font-bold text-sm">New Jersey A5328 ✓</p>
            <p className="text-xs text-green-100">Sensitive data sale ban, all entities</p>
            <p className="text-xs">June 30, 2026</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <p className="font-bold text-sm">Connecticut SB 1295 ✓</p>
            <p className="text-xs text-green-100">35K threshold, neural data, LLM disclosure</p>
            <p className="text-xs">July 1, 2026</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <p className="font-bold text-sm">Virginia · Maryland · Utah ✓</p>
            <p className="text-xs text-green-100">VA geolocation sale ban · MD inferences · UT right to correct</p>
            <p className="text-xs">July 1, 2026</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <p className="font-bold text-sm">California DROP ✓</p>
            <p className="text-xs text-green-100">Data brokers must process Delete Act requests</p>
            <p className="text-xs">August 1, 2026</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <p className="font-bold text-sm">Connecticut SB 4 ⏱</p>
            <p className="text-xs text-green-100">Geolocation sale ban, facial recognition notices</p>
            <p className="text-xs">October 1, 2026</p>
          </div>
        </div>

        <p className="text-xs font-semibold text-green-100 uppercase tracking-wide mb-2">Enacted in 2026 — Effective 2027 &amp; 2028</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4">
          <div className="bg-white/10 rounded-lg p-3">
            <p className="font-bold text-sm">Oklahoma ⏱</p>
            <p className="text-xs text-green-100">Oklahoma Consumer Data Privacy Act (SB 546)</p>
            <p className="text-xs">January 1, 2027</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <p className="font-bold text-sm">Louisiana ⏱</p>
            <p className="text-xs text-green-100">Louisiana Data Privacy Act (SB 386)</p>
            <p className="text-xs">January 1, 2027</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <p className="font-bold text-sm">Alabama ⏱</p>
            <p className="text-xs text-green-100">Alabama Personal Data Protection Act (HB 351)</p>
            <p className="text-xs">May 1, 2027</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <p className="font-bold text-sm">Vermont ⏱</p>
            <p className="text-xs text-green-100">Data Privacy &amp; Online Surveillance Act (S.71)</p>
            <p className="text-xs">January 1, 2028</p>
          </div>
        </div>

        <p className="text-xs font-semibold text-green-100 uppercase tracking-wide mb-2">Also on the 2027 Calendar</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4">
          <div className="bg-white/10 rounded-lg p-3">
            <p className="font-bold text-sm">Delaware HB 380</p>
            <p className="text-xs text-green-100">10K threshold; neural data, gov ID, inferences added</p>
            <p className="text-xs">January 1, 2027</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <p className="font-bold text-sm">New Hampshire HB 1460</p>
            <p className="text-xs text-green-100">Sale of under-13 data banned</p>
            <p className="text-xs">January 1, 2027</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <p className="font-bold text-sm">California AB 566</p>
            <p className="text-xs text-green-100">Browsers must offer opt-out preference signal</p>
            <p className="text-xs">January 1, 2027</p>
          </div>
          <div className="bg-white/10 rounded-lg p-3">
            <p className="font-bold text-sm">CT &amp; NJ data broker registries</p>
            <p className="text-xs text-green-100">CT registration Jan 1; NJ registration Apr–Jun 2027</p>
            <p className="text-xs">2027</p>
          </div>
        </div>

        <div className="p-3 bg-white/10 rounded-lg">
          <p className="text-sm font-semibold mb-1">Watch List: Bills Not Yet Enacted</p>
          <p className="text-xs">Massachusetts (Senate passed Sept 2025, House passed Jun 2026, conference committee reconciling since Jun 17, 2026), Pennsylvania (HB 78, amended in Senate committee Jun 2026), and Michigan (SB 359, in committee) are not yet enacted. This tool updates upon enactment.</p>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 pt-6 space-y-3 text-center text-sm text-gray-500">
        <p className="font-medium text-gray-700">
          © 2026 <a href="https://www.compliancehub.wiki/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">CISO Marketplace / ComplianceHub.wiki</a>
        </p>
        <p>
          Covers 23 enacted U.S. state comprehensive privacy laws (19 active) · 36 sensitive data categories · Last updated: September 2026 (Q3 2026)
        </p>
        <p className="text-xs text-gray-400 max-w-3xl mx-auto">
          This tool provides a reference overview of complex legal requirements and is not legal advice. Definitions, thresholds, and
          requirements vary by statute — always consult the official statutory text and qualified legal counsel before making compliance decisions.
        </p>
        <p className="text-xs text-gray-400 max-w-3xl mx-auto">
          <span className="font-semibold text-gray-500">Not covered here:</span> Washington My Health Data Act (consumer health data, effective 2024),
          Florida Digital Bill of Rights (effective Jul 2024, applies to $1B+ revenue entities only), Nevada opt-out law,
          Arkansas Children and Teens' Online Privacy Protection Act (HB 1717, effective Jul 1, 2026 — minors only), and
          sector-specific federal laws (HIPAA, COPPA, FCRA, GLBA). These may apply to your business independently of state comprehensive privacy laws.
        </p>
        <p className="text-xs text-gray-400">
          <span className="font-semibold text-gray-500">Pending enactment:</span> Massachusetts (House &amp; Senate both passed; conference committee since Jun 2026) · Pennsylvania (HB 78, Senate) · Michigan (SB 359) — monitor for legislative updates.
        </p>
        <div className="flex flex-wrap justify-center gap-4 text-xs text-blue-500 pt-1">
          <a href="https://childrenprivacylaws.com/" target="_blank" rel="noopener noreferrer" className="hover:underline">Children's Privacy Laws</a>
          <a href="https://biometric.myprivacy.blog/" target="_blank" rel="noopener noreferrer" className="hover:underline">Biometric Privacy Tool</a>
          <a href="https://notification.breached.company/" target="_blank" rel="noopener noreferrer" className="hover:underline">Breach Notification Tool</a>
          <a href="https://privacyrights.compliancehub.wiki/" target="_blank" rel="noopener noreferrer" className="hover:underline">Privacy Rights Hub</a>
          <a href="https://globalcompliancemap.com/" target="_blank" rel="noopener noreferrer" className="hover:underline">Global Compliance Map</a>
        </div>
      </div>
    </div>
  );
};

export default SensitiveDataComplianceTool;